/**
 * Guest details pre-filled on STAGING so testing a booking doesn't mean typing a name and an
 * address every single time (Jeremy, 2026-09-18).
 *
 * ⛔ It must never appear on production. The check is a POSITIVE match on the staging Supabase
 * project — not "is this not production", which would pre-fill a real guest's booking the moment
 * an environment variable went missing. The project ref is the environment: a production build is
 * wired to the production database and can never satisfy this, however it was deployed or
 * whatever domain it is served from. `NEXT_PUBLIC_SUPABASE_URL` is baked into the client bundle,
 * so the same check works in the browser and on the server.
 *
 * Local dev runs against staging too, and gets the pre-fill — which is the point.
 *
 * The address is deliberately @aluume.com: the comms staging guard only delivers to that domain,
 * so the confirmation actually arrives and the email itself can be checked.
 */

/** Aluume's STAGING Supabase project (prod is uakvqxmfsaudfzawccno). */
const STAGING_SUPABASE_REF = "vvnypgyaylghfqrehvhq";

export interface GuestPrefill {
  firstName: string;
  lastName: string;
  email: string;
}

/** Matches the name Jeremy's own staging test bookings have always used. */
export const STAGING_GUEST: GuestPrefill = {
  firstName: "Jeremy",
  lastName: "Monaghan",
  email: "jeremy@aluume.com",
};

/** True only when this build is wired to the staging database. */
export function isStagingBuild(): boolean {
  return (process.env.NEXT_PUBLIC_SUPABASE_URL ?? "").includes(STAGING_SUPABASE_REF);
}

/**
 * The guest details a NEW booking form should start with, or null on production (and anywhere
 * else the database is not staging). Callers use it for the initial value only — never to
 * overwrite something already typed, and never on a booking that already has a guest.
 */
export function stagingGuestPrefill(): GuestPrefill | null {
  return isStagingBuild() ? { ...STAGING_GUEST } : null;
}
